import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import StatusBadge from "../../components/StatusBadge";
import { Plus, Truck, Gauge, Pencil, Trash2, X, Calendar } from "lucide-react";
import TrailerBookingModal from "../../components/trailers/TrailerBookingModal";

const TRAILER_STATUSES = ["tillganglig", "bokad", "utlamnad", "service", "trasig"];
const EMPTY_FORM = {
  regnr: "", namn: "", brand: "", model: "", year: "",
  kapacitet_kg: "", storlek: "", plats: "", status: "tillganglig",
  enable_rental_reporting: false, hourly_rate: "",
};

export default function Trailers() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [filter, setFilter] = useState({ status: "", q: "" });
  const [bookingTrailer, setBookingTrailer] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.q) params.q = filter.q;
      const { data } = await api.get("/trailers", { params });
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (user) load(); }, [user, filter.status]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      regnr: m.regnr || "", namn: m.namn || "", brand: m.brand || "", model: m.model || "",
      year: m.year || "", kapacitet_kg: m.kapacitet_kg ?? "", storlek: m.storlek || "",
      plats: m.plats || "", status: m.status || "tillganglig",
      enable_rental_reporting: !!m.enable_rental_reporting,
      hourly_rate: m.hourly_rate ?? "",
    });
    setShowForm(true);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 50);
  };

  const cancelForm = () => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        regnr: form.regnr.toUpperCase().trim(),
        year: form.year ? Number(form.year) : null,
        kapacitet_kg: form.kapacitet_kg !== "" ? Number(form.kapacitet_kg) : null,
        hourly_rate: form.hourly_rate !== "" ? Number(form.hourly_rate) : null,
      };
      if (editingId) {
        await api.patch(`/trailers/${editingId}`, payload);
        toast.success(t("trl.updated"));
      } else {
        await api.post("/trailers", payload);
        toast.success(t("trl.created"));
      }
      cancelForm();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Ta bort denna släpvagn?")) return;
    try {
      await api.delete(`/trailers/${id}`);
      toast.success(t("trl.deleted"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const canEdit = (m) => user.role === "admin" || user.user_id === m.owner_id;

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-tech">{t("trl.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("trl.title")}</h1>
          </div>
          <button onClick={() => { if (showForm) cancelForm(); else setShowForm(true); }} className="btn-primary" data-testid="trailer-new-btn">
            <Plus size={14} /> {t("trl.new")}
          </button>
        </div>

        <VehiclesSubNav />

        <div className="flex flex-wrap gap-3 mb-6">
          <input
            placeholder={`${t("market.search")}: ${t("veh.regnr")}`}
            className="input-tech max-w-[260px]"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onBlur={() => load()}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="trailer-search-input"
          />
          <select className="input-tech max-w-[200px]" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="trailer-status-filter">
            <option value="">{t("veh.status")}: —</option>
            {TRAILER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {showForm && (
          <form onSubmit={submit} className="mb-8 border border-[var(--ink-200)] bg-[var(--bone)] p-5 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="trailer-form">
            <div className="md:col-span-3 label-tech">{editingId ? "Redigera släpvagn" : t("trl.new")}</div>
            <div><div className="label-tech mb-2">{t("veh.regnr")} *</div>
              <input className="input-tech" required value={form.regnr} onChange={(e) => setForm({ ...form, regnr: e.target.value.toUpperCase() })} data-testid="trailer-regnr-input" /></div>
            <div><div className="label-tech mb-2">{t("trl.namn")}</div>
              <input className="input-tech" value={form.namn} onChange={(e) => setForm({ ...form, namn: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.brand")}</div>
              <input className="input-tech" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.model")}</div>
              <input className="input-tech" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("veh.year")}</div>
              <input type="number" className="input-tech" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("trl.kapacitet")}</div>
              <input type="number" className="input-tech" value={form.kapacitet_kg} onChange={(e) => setForm({ ...form, kapacitet_kg: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("trl.storlek")}</div>
              <input className="input-tech" placeholder="2.5 x 1.5 m" value={form.storlek} onChange={(e) => setForm({ ...form, storlek: e.target.value })} /></div>
            <div><div className="label-tech mb-2">{t("mach.plats")}</div>
              <input className="input-tech" value={form.plats} onChange={(e) => setForm({ ...form, plats: e.target.value })} /></div>
            {editingId && (
              <div><div className="label-tech mb-2">{t("veh.status")}</div>
                <select className="input-tech" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  {TRAILER_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            {/* Rental reporting */}
            <div className="md:col-span-3 border-t border-[var(--ink-200)] pt-4 mt-2">
              <label className="flex items-start gap-3 cursor-pointer" data-testid="trailer-rental-toggle">
                <input
                  type="checkbox"
                  checked={form.enable_rental_reporting}
                  onChange={(e) => setForm({ ...form, enable_rental_reporting: e.target.checked })}
                  className="mt-1"
                  data-testid="trailer-rental-checkbox"
                />
                <div>
                  <div className="font-medium text-sm">{t("rental.toggle")}</div>
                  <div className="text-xs text-[var(--ink-500)] mt-1 max-w-xl">{t("rental.toggleHelp")}</div>
                </div>
              </label>
              {form.enable_rental_reporting && (
                <div className="mt-3 max-w-xs">
                  <div className="label-tech mb-2">{t("rental.hourlyRate")}</div>
                  <input type="number" step="1" className="input-tech" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} data-testid="trailer-hourly-rate-input" />
                </div>
              )}
            </div>

            <div className="md:col-span-3 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={cancelForm}><X size={14} /> {t("admin.orgs.cancel")}</button>
              <button type="submit" className="btn-primary" data-testid="trailer-submit-btn">{t("admin.orgs.save")}</button>
            </div>
          </form>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--ink-200)] border border-[var(--ink-200)]">
          {loading ? (
            <div className="bg-[var(--surface)] p-6 label-tech">…</div>
          ) : items.length === 0 ? (
            <div className="bg-[var(--surface)] p-10 label-tech md:col-span-2 lg:col-span-3">{t("trl.empty")}</div>
          ) : items.map((m) => (
            <div key={m.id} className="bg-[var(--surface)] p-5 flex flex-col gap-2" data-testid={`trailer-card-${m.id}`}>
              <div className="flex items-center gap-2 label-tech"><Truck size={12} /> {m.kapacitet_kg ? `${m.kapacitet_kg} kg` : ""} {m.storlek ? `· ${m.storlek}` : ""}</div>
              <div className="font-display text-lg font-mono-tech">{m.regnr}</div>
              <div className="text-sm text-[var(--ink-700)]">{m.namn || `${m.brand || ""} ${m.model || ""}`.trim()}</div>
              <div className="flex items-center gap-3 mt-1 text-xs">
                <StatusBadge status={m.status} />
                {m.enable_rental_reporting && (
                  <span className="text-[10px] font-mono-tech uppercase tracking-wider text-[var(--clay)]" data-testid={`trailer-rental-badge-${m.id}`}>
                    Uthyrning · {m.hourly_rate || 0} kr/h
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <button onClick={() => setBookingTrailer(m)} className="btn-ghost text-xs" data-testid={`trailer-book-btn-${m.id}`}>
                  <Calendar size={12} /> {t("trl.book")}
                </button>
                {canEdit(m) && (
                  <>
                    <button onClick={() => startEdit(m)} className="btn-ghost text-xs"><Pencil size={12} /></button>
                    <button onClick={() => remove(m.id)} className="btn-ghost text-xs text-[var(--brick)]"><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {bookingTrailer && (
          <TrailerBookingModal
            trailer={bookingTrailer}
            onClose={() => setBookingTrailer(null)}
            onCreated={() => { setBookingTrailer(null); load(); }}
          />
        )}
      </div>
    </Layout>
  );
}
